import {
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { SortDto, TenantFilterDto, TenantItemDto, TenantService } from '@artemis-software/wr-api';
import { debounceTime, distinctUntilChanged, firstValueFrom, skip } from 'rxjs';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { NbComponentSize, NbPopoverDirective } from '@nebular/theme';
import { watch } from '@/utils/watch';
import { TenantTypePipe } from '@shared/pipes/tenant-type.pipe';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-tenant-select',
  templateUrl: './tenant-select.component.html',
  styleUrls: ['./tenant-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TenantSelectComponent),
      multi: true,
    },
  ],
})
export class TenantSelectComponent extends AbstractFormControl<string> {
  readonly tenantService = inject(TenantService);
  readonly tenantTypePipe = inject(TenantTypePipe);

  readonly popover = viewChild(NbPopoverDirective);

  readonly size = input<NbComponentSize>('medium');
  readonly tenantType = input<TenantFilterDto.TypeEnum | undefined>();

  readonly showEmptyOption = input<boolean>(false);
  readonly initialTenant = input<TenantItemDto | undefined>();
  readonly placeholder = input<string | undefined>();

  readonly resetVersion = signal(0);
  readonly filterText = signal('');
  readonly page = signal(0);
  readonly tenants = signal<TenantItemDto[]>([]);
  readonly loading = signal(false);
  readonly hasMore = signal(true);
  readonly unresolvedId = signal<string | undefined>(undefined);
  private pendingFindByIdFor?: string;

  readonly placeholderText = computed(
    () => this.placeholder() ?? `${this.tenantTypePipe.transform(this.tenantType())} auswählen`,
  );

  constructor() {
    super();
    watch([this.tenantType], async () => {
      await this.reset();
    });

    watch([this.unresolvedId, this.initialTenant, this.tenants], () => {
      this.resolveUnresolvedTenantName();
    });

    toObservable(this.filterText)
      .pipe(skip(1), debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        void this.reset();
      });
  }

  override writeValue(id: string | undefined): void {
    super.writeValue(id);
    if (id) {
      const known = this.findKnownTenant(id);
      if (known) {
        this.filterText.set(known.name);
        this.unresolvedId.set(undefined);
      } else {
        this.unresolvedId.set(id);
      }
    } else {
      this.filterText.set('');
      this.unresolvedId.set(undefined);
    }
  }

  private resolveUnresolvedTenantName(): void {
    const id = this.unresolvedId();
    if (!id) return;

    const known = this.findKnownTenant(id);
    if (known) {
      this.filterText.set(known.name);
      this.unresolvedId.set(undefined);
      return;
    }

    if (this.pendingFindByIdFor === id) return;
    this.pendingFindByIdFor = id;
    firstValueFrom(this.tenantService.findById(id))
      .then(tenant => {
        if (this.value === id && this.unresolvedId() === id) {
          this.filterText.set(tenant.name);
          this.unresolvedId.set(undefined);
        }
      })
      .catch(err => {
        console.error(`Tenant ${id} konnte für die Anzeige nicht geladen werden`, err);
      })
      .finally(() => {
        if (this.pendingFindByIdFor === id) {
          this.pendingFindByIdFor = undefined;
        }
      });
  }

  private findKnownTenant(id: string): TenantItemDto | undefined {
    const fromList = this.tenants().find(t => t.id === id);
    if (fromList) return fromList;
    const initial = this.initialTenant();
    return initial?.id === id ? initial : undefined;
  }

  onSelectionChange(tenant: TenantItemDto): void {
    this.filterText.set(tenant.name);
    this.unresolvedId.set(undefined);
    this.value = tenant.id;
    this.popover()?.hide();
  }

  async onClear(): Promise<void> {
    this.value = undefined;
    this.filterText.set('');
    this.unresolvedId.set(undefined);
    await this.reset();
  }

  onAllSelected(): void {
    this.value = undefined;
    this.filterText.set('');
    this.unresolvedId.set(undefined);
    this.popover()?.hide();
    this.reset();
  }

  async reset(): Promise<void> {
    this.resetVersion.update(v => v + 1);
    this.tenants.set([]);
    this.page.set(0);
    this.hasMore.set(true);
    this.loading.set(false);
    await this.loadMore();
  }

  async loadMore(): Promise<void> {
    if (this.loading() || !this.hasMore()) return;
    this.loading.set(true);

    const versionAtStart = this.resetVersion();
    const page = this.page();

    try {
      const more = await firstValueFrom(this.tenantService.findAll({
        text: this.filterText(),
        type: this.tenantType(),
        page: page,
        size: 20,
        sort: { sort: 'name', order: OrderEnum.Asc },
      }));

      if (this.resetVersion() !== versionAtStart) return;

      this.page.update(p => p + 1);
      if (more.length < 20) this.hasMore.set(false);
      this.tenants.update(current => [...current, ...more]);
      if (this.value && !this.filterText()) {
        const found = this.tenants().find(t => t.id === this.value);
        if (found) {
          this.filterText.set(found.name);
        }
      }
    } finally {
      if (this.resetVersion() === versionAtStart) this.loading.set(false);
    }
  }
}
